import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { actualizarConversacionPlantelService } from "../../../services/plantels/conversaciones.service";
import Loader from "../../UI/Loader";
import Field from "../../UI/Field";
import AuditInfo from "../../UI/AuditInfo";
import Modal from "../../UI/Modal";

/* ------------------------------------------------------------------ */
/*  Modal para Ver / Editar una conversación de plantel                */
/* ------------------------------------------------------------------ */

const ConversacionPlantelModal = ({
  open,
  onClose,
  conversacion,
  initialMode = "view", // "view" | "edit"
  onSaved,
}) => {
  const [modo, setModo] = useState("view");
  const [editPregunta, setEditPregunta] = useState("");
  const [editDescripcion, setEditDescripcion] = useState("");
  const [editProposito, setEditProposito] = useState("");
  const [editUtilizar, setEditUtilizar] = useState("");
  const [saving, setSaving] = useState(false);

  /* ----- Inicializar campos cuando se abre el modal ----- */
  useEffect(() => {
    if (open && conversacion) {
      setEditPregunta(conversacion.pregunta || "");
      setEditDescripcion(conversacion.descripcion || "");
      setEditProposito(conversacion.descripcion_proposito || "");
      setEditUtilizar(conversacion.descripcion_utilizar || "");
      setModo(initialMode);
    }
  }, [open, conversacion, initialMode]);

  /* ----- Cerrar ----- */
  const cerrar = () => {
    setModo("view");
    onClose();
  };

  /* ----- Guardar cambios (PATCH) ----- */
  const guardarCambios = async () => {
    if (!conversacion) return;
    setSaving(true);
    try {
      const payload = {
        pregunta: editPregunta,
        descripcion: editDescripcion,
        descripcion_proposito: editProposito,
        descripcion_utilizar: editUtilizar,
      };
      await actualizarConversacionPlantelService(conversacion.id, payload);
      toast.success("Conversación actualizada exitosamente.");
      cerrar();
      if (onSaved) onSaved();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        "Error al guardar los cambios.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!conversacion) return null;

  return (
    <Modal
      open={open}
      onClose={cerrar}
      title={
        conversacion
          ? `Conversación ${conversacion.numero_conversacion} — ${conversacion.pregunta || ""}`
          : ""
      }
      size="lg"
    >
      {modo === "view" ? (
        /* ---------- MODO LECTURA ---------- */
        <div className="row g-4">
          <div className="col-12">
            <Field
              icon="mdi:help-circle-outline"
              label="Pregunta"
              value={editPregunta}
              justify
            />
          </div>
          <div className="col-12">
            <Field
              icon="mdi:text-box-outline"
              label="Descripción"
              value={editDescripcion}
              justify
            />
          </div>
          <div className="col-12">
            <Field
              icon="mdi:target"
              label="Propósito"
              value={editProposito}
              justify
            />
          </div>
          <div className="col-12">
            <Field
              icon="mdi:lightbulb-outline"
              label="Cómo utilizar"
              value={editUtilizar}
              justify
            />
          </div>

          {/* Auditoría */}
          <div className="col-12">
            <AuditInfo
              createdAt={conversacion.created_at}
              updatedAt={conversacion.updated_at}
              createdBy={conversacion.created_by}
              updatedBy={conversacion.updated_by}
            />
          </div>

          {/* Botón para pasar a editar */}
          <div className="col-12">
            <button
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2 w-100"
              onClick={() => setModo("edit")}
            >
              <Icon icon="mdi:pencil-outline" width="18" />
              Editar esta conversación
            </button>
          </div>
        </div>
      ) : (
        /* ---------- MODO EDICIÓN ---------- */
        <div className="row g-4">
          <div className="col-12">
            <label className="form-label fw-medium d-flex align-items-center gap-2">
              <Icon icon="mdi:help-circle-outline" width={16} className="text-primary" />
              Pregunta
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={editPregunta}
              onChange={(e) => setEditPregunta(e.target.value)}
              style={{ fontSize: "14px", resize: "vertical" }}
            />
          </div>
          <div className="col-12">
            <label className="form-label fw-medium d-flex align-items-center gap-2">
              <Icon icon="mdi:text-box-outline" width={16} className="text-primary" />
              Descripción
            </label>
            <textarea
              className="form-control"
              rows={4}
              value={editDescripcion}
              onChange={(e) => setEditDescripcion(e.target.value)}
              style={{ fontSize: "14px", resize: "vertical" }}
            />
          </div>
          <div className="col-12">
            <label className="form-label fw-medium d-flex align-items-center gap-2">
              <Icon icon="mdi:target" width={16} className="text-primary" />
              Propósito
            </label>
            <textarea
              className="form-control"
              rows={4}
              value={editProposito}
              onChange={(e) => setEditProposito(e.target.value)}
              style={{ fontSize: "14px", resize: "vertical" }}
            />
          </div>
          <div className="col-12">
            <label className="form-label fw-medium d-flex align-items-center gap-2">
              <Icon icon="mdi:lightbulb-outline" width={16} className="text-primary" />
              Cómo utilizar
            </label>
            <textarea
              className="form-control"
              rows={4}
              value={editUtilizar}
              onChange={(e) => setEditUtilizar(e.target.value)}
              style={{ fontSize: "14px", resize: "vertical" }}
            />
          </div>

          {/* Botones de acción */}
          <div className="col-12 d-flex gap-2">
            <button
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2 flex-grow-1"
              onClick={() => setModo("view")}
              disabled={saving}
            >
              <Icon icon="mdi:arrow-left" width="16" />
              Cancelar
            </button>
            <button
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2 flex-grow-1"
              onClick={guardarCambios}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader type="spinner" size="sm" delay={0} />
                  Guardando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:content-save-outline" width="18" />
                  Guardar cambios
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ConversacionPlantelModal;
